import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { usePodcastFeeds } from '../hooks';
import { Button, Card, Modal, Spinner, SectionHeader } from '../components/common';
import FeedSummaryCard from '../components/feed/FeedSummaryCard';
import { PodcastFeedSummary } from '../types';

export function UnifiedFeedsPage() {
  const navigate = useNavigate();
  const { feeds, loading: feedsLoading, error: feedsError, refetch, createFeed, deleteFeed } = usePodcastFeeds();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!createName.trim()) {
      setCreateError('Name is required');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createFeed({
        name: createName.trim(),
        description: createDescription.trim() || undefined,
        published: false,
      });
      setIsCreateOpen(false);
      setCreateName('');
      setCreateDescription('');
      navigate(`/feeds/${created.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create feed');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (feed: PodcastFeedSummary) => {
    if (
      !window.confirm(
        `Delete the feed "${feed.name}"? Subscribers will lose access. This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await deleteFeed(feed.id);
    } catch (err) {
      console.error('Failed to delete feed:', err);
      window.alert('Failed to delete feed. Please try again.');
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fade-in">
        <SectionHeader
          eyebrow="feeds"
          title="Your podcast feeds"
          description="Manage your feeds and add conversions from your collection."
          className="mb-12"
        />

        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-normal text-foreground tracking-tight">
            {feedsLoading ? 'Loading…' : `${feeds.length + 1} feed${feeds.length === 0 ? '' : 's'}`}
          </h2>
          <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
            New feed
          </Button>
        </div>

        {feedsError && (
          <Card className="mb-6 border-destructive/40">
            <p className="font-body text-destructive mb-3">{feedsError}</p>
            <Button variant="secondary" size="sm" onClick={refetch}>
              Try again
            </Button>
          </Card>
        )}

        {feedsLoading && feeds.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="space-y-4">
            <FeedSummaryCard
              name="Default Feed"
              description="Your personal podcast feed. Available in any podcast app."
              badge={{ label: 'active', tone: 'ok' }}
              onOpen={() => navigate('/feeds/default')}
            />

            {feeds.map((feed) => (
              <FeedSummaryCard
                key={feed.id}
                name={feed.name}
                description={feed.description}
                badge={
                  feed.published
                    ? { label: 'published', tone: 'ok' }
                    : { label: 'draft', tone: 'off' }
                }
                itemCount={feed.itemCount}
                onOpen={() => navigate(`/feeds/${feed.id}`)}
                onDelete={() => handleDelete(feed)}
              />
            ))}

            {feeds.length === 0 && (
              <Card>
                <div className="text-center py-12">
                  <h3 className="font-display text-xl font-medium text-foreground">
                    No custom feeds yet
                  </h3>
                  <p className="mt-2 font-body text-muted-foreground">
                    Create your first curated feed to get started.
                  </p>
                  <Button
                    variant="primary"
                    className="mt-6"
                    onClick={() => setIsCreateOpen(true)}
                  >
                    Create a feed
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={isCreateOpen}
        onClose={() => {
          if (!creating) setIsCreateOpen(false);
        }}
        title="Create a curated feed"
      >
        <div className="space-y-4">
          <div>
            <label className="font-body text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
              Name
            </label>
            <input
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              maxLength={200}
              placeholder="e.g. Weekly reading list"
              className="w-full px-3 py-2 bg-background border border-border rounded-sm font-body text-sm text-foreground"
            />
          </div>
          <div>
            <label className="font-body text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
              Description
            </label>
            <textarea
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="Short summary shown to listeners in podcast apps."
              className="w-full px-3 py-2 bg-background border border-border rounded-sm font-body text-sm text-foreground"
            />
          </div>

          {createError && (
            <p className="font-body text-sm text-destructive">{createError}</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => setIsCreateOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating…' : 'Create feed'}
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
